import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ArcComponent, ArcOptions } from '@cmps/ng-chart';

import {
    CmpsDecimalWithSuffixPipe,
    CmpsPercentPipe,
} from '../../../common/number.pipe';
import { CoachingInsightWidgetService } from '../../oa/services/coaching-insight.widget.service';
import { InsightsSummaryDataKpi } from '../model/insights-summary-data-kpi.model';
import { InsightsSummaryDataPeriodTypes } from '../model/insights-summary-data-period-types.model';
import { InsightsSummaryData } from '../model/insights-summary-data.model';
import { SelectionChangeEvent } from '../model/selection-change-event.model';

const arcMaxFontSize = 23;
const arcHeight = 100;
const arcInnerRadius = 0.88;
const arcInnerRadiusSelected = 0.82;

@Component({
    selector: '[cmpsCoachingNavigationBar]',
    templateUrl: './navigation-bar.component.html',
})
export class CoachingNavigationBarComponent implements OnChanges, OnDestroy {
    @ViewChildren(ArcComponent) private readonly _arcs: QueryList<ArcComponent>;

    @Input() insightSummaryData: InsightsSummaryData;
    @Input() selectedPeriodType: InsightsSummaryDataPeriodTypes;
    @Input() selectedKpi: InsightsSummaryDataKpi;
    @Output() selectionChange: EventEmitter<SelectionChangeEvent>;

    /** @private */ _arcOptsList: ArcOptions[];
    /** @private */ _arcDataList: any[];

    constructor(
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _percentPipe: CmpsPercentPipe,
        private readonly _coachInsightService: CoachingInsightWidgetService,
        private readonly _decimalWithSuffixPipe: CmpsDecimalWithSuffixPipe,
    ) {
        this.selectionChange = new EventEmitter();
        this._arcOptsList = [];
        this._arcDataList = [];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(changes: SimpleChanges) {
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('insightSummaryData')) {
            this._drawCircle();
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('selectedKpi')) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            this._setRadius(
                // eslint-disable-next-line @typescript-eslint/dot-notation
                changes['selectedKpi'].previousValue,
                arcInnerRadius,
            );
            this._setRadius(this.selectedKpi, arcInnerRadiusSelected);
        }
    }

    ngOnDestroy(): void {
        this.selectionChange.complete();
    }

    /** @private */
    _onSelectClick(kpi: InsightsSummaryDataKpi): void {
        this._setRadius(this.selectedKpi, arcInnerRadius);
        this._setRadius(kpi, arcInnerRadiusSelected);
        this.selectedKpi = kpi;

        this.selectionChange.emit({
            kpi: this.selectedKpi,
            periodType: this.selectedPeriodType,
        });
    }

    private _drawCircle(): void {
        this._arcOptsList = [];
        this._arcDataList = [];

        if (!this.insightSummaryData || !this.insightSummaryData.kpis) {
            return;
        }

        this.insightSummaryData.kpis.forEach((kpi) => {
            const label = {
                formatter: () =>
                    kpi.kpiValue == null
                        ? '-'
                        : this._percentPipe.transform(
                              kpi.kpiValue / 100,
                              kpi.kpiFormat,
                          ),
                maxFontSize: arcMaxFontSize,
            };

            const percentToTarget = kpi.kpiValue * 0.01;
            const color = kpi.kpiColour;

            const arcOpts: ArcOptions = {
                rect: { width: '100%', height: arcHeight, top: 5, bottom: 5 },
                label,
                innerRadius:
                    kpi === this.selectedKpi
                        ? arcInnerRadiusSelected
                        : arcInnerRadius,
            };
            this._arcOptsList.push(arcOpts);

            const arcData = [
                {
                    x: '', // TODO revise
                    y: percentToTarget,
                    color,
                },
                {
                    x: 'gap', // TODO revise
                    y: Math.min(Math.max(1 - percentToTarget, 0), 1),
                    color,
                },
            ];
            this._arcDataList.push(arcData);
        });
    }

    private _setRadius(kpi: InsightsSummaryDataKpi, innerRadius: number): void {
        try {
            const index = this.insightSummaryData.kpis.indexOf(kpi);
            const next = { ...this._arcOptsList[index], innerRadius };
            const arc = this._arcs.find(
                (e) => e.options === this._arcOptsList[index],
            );
            this._arcOptsList[index] = arc.options = next;
            arc.render(false);
        } catch {
            // nothing
        }
    }
}
